import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  sendCodeToPhone, 
  sendCodeToPhoneStatusClear, 
  createTokenBySession,
  clearCreateTokenStatus,
  logout,
  clearAllState,
  getUserProfile,
  giveRoleToNewUser,
} from '../redux/reducers/utils/authReducer';
import { setCurrentRoute } from '../redux/reducers/utils/navReducer';
import {
  sendRequestAccessGetComponentsForUser,
} from '../redux/reducers/utils/servicesReducers/servicesReducer';
import {
  getUserInfo,
} from '../redux/reducers/utils/settingsReducers/settingsProfileReducer';
import { sendRequestKitchens } from '../redux/reducers/utils/kitchenReducers/kitchenReducer';

/**
 * Универсальный хук для управления авторизацией
 * Включает в себя:
 * - Процесс авторизации (отправка кода, верификация)
 * - Управление сессией (logout, очистка данных)
 * - Инициализация данных пользователя после авторизации
 * - Управление состоянием авторизации
 */
const useAuth = () => {
  const dispatch = useDispatch();
  const { 
    sendCodeStatus, 
    createTokenStatus, 
    logoutStatus,
    isAuth,
    userProfile,
    isNewUser,
    giveRoleToNewUserRequestStatus,
    userProfileRequestStatus
  } = useSelector((store) => store.auth);
  
  // Локальное состояние для формы авторизации
  const [phoneNumber, setPhoneNumber] = useState('');
  const [lastCodeRequestTime, setLastCodeRequestTime] = useState(null);
  const [remainingCooldown, setRemainingCooldown] = useState(0);

  // Генерация или получение machine ID
  const getMachineId = useCallback(() => {
    let machineId = localStorage.getItem('MachineId');
    if (!machineId) {
      machineId = crypto.randomUUID();
      localStorage.setItem('MachineId', machineId);
    }
    return machineId;
  }, []);

  // Отправка кода
  const sendCode = useCallback(async (data) => {
    if (!data || !data.phone) throw new Error('Номер телефона не указан');
    
    const requestData = {
      phone: data.phone,
      device: getMachineId(),
      sendCodeBy: data.SendCodeBy || 'Telegram',
    };

    try {
      const result = await dispatch(sendCodeToPhone(requestData)).unwrap();
      setPhoneNumber(data.phone);
      setLastCodeRequestTime(Date.now());
      return result;
    } catch (error) {
      console.error('Ошибка отправки кода:', error);
      throw error;
    }
  }, [dispatch, getMachineId]);

  // Повторная отправка кода
  const resendCode = useCallback(async () => {
    if (!phoneNumber) throw new Error('Номер телефона не найден');
    
    const requestData = {
      phone: phoneNumber,
      device: getMachineId(),
    };

    try {
      const result = await dispatch(sendCodeToPhone(requestData)).unwrap();
      setLastCodeRequestTime(Date.now());
      return result;
    } catch (error) {
      console.error('Ошибка повторной отправки кода:', error);
      throw error;
    }
  }, [dispatch, phoneNumber, getMachineId]);

  // Проверка кода и создание токена
  const verifyCode = useCallback(async (code) => {
    if (!code || code.length !== 4) {
      throw new Error('Код должен содержать 4 цифры');
    }

    const requestData = {
      session: localStorage.getItem('session') || '',
      code: code,
    };

    try {
      const result = await dispatch(createTokenBySession(requestData)).unwrap();
      return result;
    } catch (error) {
      console.error('Ошибка верификации кода:', error);
      throw error;
    }
  }, [dispatch]);

  // Выход с полной очисткой
  const handleLogout = useCallback(async () => {
    const requestData = {
      requestTimestamp: Date.now(),
    };

    try {
      // Пытаемся выполнить серверный logout
      await dispatch(logout(requestData)).unwrap();
    } catch (error) {
      console.error('Ошибка серверного logout:', error);
      // Продолжаем с локальной очисткой даже при ошибке сервера
    } finally {
      // В любом случае очищаем локальное состояние
      try {
        dispatch(clearAllState());
      } catch (cleanupError) {
        console.error('Ошибка очистки локального состояния:', cleanupError);
      }
    }
  }, [dispatch]);

  // Инициализация данных пользователя после авторизации
  const initializeUserData = useCallback(async () => {
    if (!isAuth) return;

    const requestTimestamp = Date.now();
    
    try {
      await Promise.all([
        // ...(isNewUser ? [dispatch(giveRoleToNewUser({ requestTimestamp }))] : []),
        dispatch(giveRoleToNewUser({ requestTimestamp })),
        dispatch(getUserProfile({ requestTimestamp })),
        dispatch(sendRequestAccessGetComponentsForUser({ requestTimestamp })),
        dispatch(getUserInfo({ requestTimestamp })),
        dispatch(sendRequestKitchens({ requestTimestamp: Date.now(), offset: 0, limit: 10000 })),
      ]);
    } catch (error) {
      console.error('Ошибка инициализации данных пользователя:', error);
    }
  }, [isAuth, dispatch]);

  // Обработка неавторизованного состояния
  const handleUnauthorized = useCallback(() => {
    dispatch(setCurrentRoute('/auth'));
    localStorage.removeItem('token');
    localStorage.removeItem('session');
  }, [dispatch]);

  // Проверка cooldown
  const isCooldownExpired = useCallback(() => {
    if (!lastCodeRequestTime) return true;
    const currentTime = Date.now();
    return currentTime - lastCodeRequestTime > 60000; // 60 секунд
  }, [lastCodeRequestTime]);

  // Сброс состояний при размонтировании
  const resetStates = useCallback(() => {
    setPhoneNumber('');
    setLastCodeRequestTime(null);
    setRemainingCooldown(0);
  }, []);

  // Обновление таймера cooldown
  useEffect(() => {
    if (!lastCodeRequestTime) {
      setRemainingCooldown(0);
      return;
    }

    const updateRemainingCooldown = () => {
      if (isCooldownExpired()) {
        setRemainingCooldown(0);
      } else {
        const currentTime = Date.now();
        const cooldownDuration = 60000;
        const timeSinceLastRequest = currentTime - lastCodeRequestTime;
        const remainingTime = cooldownDuration - timeSinceLastRequest;
        setRemainingCooldown(Math.ceil(remainingTime / 1000));
      }
    };

    updateRemainingCooldown(); // Сразу обновляем
    const intervalId = setInterval(updateRemainingCooldown, 1000);
    
    return () => clearInterval(intervalId);
  }, [lastCodeRequestTime, isCooldownExpired]);

  // Управление состоянием авторизации и инициализация данных
  useEffect(() => {
    if (isAuth) {
      initializeUserData();
    } else {
      handleUnauthorized();
    }
  }, [isAuth, initializeUserData, handleUnauthorized]);

  // Сброс статусов при монтировании (для очистки после перезагрузки или зависших состояний)
  useEffect(() => {
    // Очищаем статусы при монтировании хука (когда пользователь попадает на страницу авторизации)
    dispatch(sendCodeToPhoneStatusClear());
    dispatch(clearCreateTokenStatus());
    
    // Также очищаем при размонтировании
    return () => {
      dispatch(sendCodeToPhoneStatusClear());
      dispatch(clearCreateTokenStatus());
    };
  }, [dispatch]);

  // Автоматический сброс статуса ошибки sendCode через 3 секунды
  useEffect(() => {
    if (sendCodeStatus === 'rejected') {
      const timer = setTimeout(() => {
        dispatch(sendCodeToPhoneStatusClear());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [sendCodeStatus, dispatch]);

  // Автоматический сброс статуса ошибки createToken через 3 секунды
  useEffect(() => {
    if (createTokenStatus === 'rejected') {
      const timer = setTimeout(() => {
        dispatch(clearCreateTokenStatus());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [createTokenStatus, dispatch]);

  return {
    // Состояния авторизации
    sendCodeStatus,
    createTokenStatus,
    logoutStatus,
    isAuth,
    userProfile,
    isNewUser,
    
    // Состояния загрузки данных
    giveRoleToNewUserRequestStatus,
    userProfileRequestStatus,
    
    // Состояния формы авторизации
    phoneNumber,
    remainingCooldown,
    
    // Методы авторизации
    sendCode,
    resendCode,
    verifyCode,
    handleLogout,
    
    // Методы управления данными
    initializeUserData,
    
    // Утилиты
    isCooldownExpired,
    resetStates,
    setPhoneNumber,
    getMachineId,
  };
};

export default useAuth;
